import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import * as usersService from '../services/api/users';
import { transformUser } from '../services/transformers/userTransformer';
import { getUserId } from '../services/api/config';
import { ArrowLeftIcon, PhoneIcon, MailIcon, LockIcon, EditIcon, QuestionIcon, DoorIcon, ChevronRightIcon, SunIcon, MoonIcon } from '../components/Icons';
import './Account.css';

const Account = () => {
  const navigate = useNavigate();
  const { user, logout } = useApp();
  const { theme, toggleTheme } = useTheme();
  const [image, setImage] = useState(user?.image || null);
  const [userData, setUserData] = useState(user);
  const [userLoading, setUserLoading] = useState(true);

  // Fetch fresh user data from API when component mounts
  useEffect(() => {
    const fetchUserData = async () => {
      const userId = getUserId();
      if (!userId) {
        setUserLoading(false);
        return;
      }

      try {
        setUserLoading(true);
        const result = await usersService.getUserById(userId);
        
        if (result.success && result.data) {
          const transformedUser = transformUser(result.data);
          setUserData(transformedUser);
          setImage(transformedUser.image || null);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setUserLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Update local state when user from context changes
  useEffect(() => {
    if (user) {
      setUserData(user);
      setImage(user.image || null);
    }
  }, [user]);

  if (!user && !userLoading) {
    navigate('/login');
    return null;
  }

  if (userLoading) {
    return (
      <div className="account-screen">
        <div className="account-container">
          <div className="account-header">
            <button className="header-back-btn" onClick={() => navigate('/dashboard')}>
              <ArrowLeftIcon size={24} color={theme === 'dark' ? '#ffffff' : '#1a1a1a'} />
            </button>
            <h1 className="header-title">Account Settings</h1>
            <div style={{ width: '40px' }}></div>
          </div>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>Loading user data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return null;
  }

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '+1 (555) 000-1234';
    // Format phone number if needed
    return phone;
  };

  return (
    <div className="account-screen">
      <div className="account-container">
        <div className="account-header">
          <button className="header-back-btn" onClick={() => navigate('/dashboard')}>
            <ArrowLeftIcon size={24} color={theme === 'dark' ? '#ffffff' : '#1a1a1a'} />
          </button>
          <h1 className="header-title">Account Settings</h1>
          <div style={{ width: '40px' }}></div>
        </div>

        <div className="profile-section-new">
          <div className="profile-image-wrapper-new">
            <div className="profile-image-display">
              {image ? (
                <img src={image} alt="Profile" className="profile-image-new" />
              ) : (
                <div className="profile-placeholder-new">
                  <span>👤</span>
                </div>
              )}
            </div>
          </div>
          <h2 className="profile-name">{userData.name || 'Alex Johnson'}</h2>
          <div className="certified-badge">
            CERTIFIED PICKER
          </div>
        </div>

        <div className="account-sections">
          <div className="section-group">
            <div className="section-label">PERSONAL INFORMATION</div>
            <div className="info-card-new">
              <div className="info-item" onClick={() => {/* Navigate to edit phone */}}>
                <div className="info-item-left">
                  <div className="info-icon-wrapper phone">
                    <PhoneIcon size={20} color="#ffffff" />
                  </div>
                  <div className="info-item-content">
                    <div className="info-item-label">Phone Number</div>
                    <div className="info-item-value">{formatPhoneNumber(userData.phone)}</div>
                  </div>
                </div>
                <ChevronRightIcon size={20} color="#9ca3af" />
              </div>
              <div className="info-item" onClick={() => {/* Navigate to edit email */}}>
                <div className="info-item-left">
                  <div className="info-icon-wrapper email">
                    <MailIcon size={20} color="#ffffff" />
                  </div>
                  <div className="info-item-content">
                    <div className="info-item-label">Email Address</div>
                    <div className="info-item-value">{userData.email || 'alex.johnson@plasticdaddy.com'}</div>
                  </div>
                </div>
                <ChevronRightIcon size={20} color="#9ca3af" />
              </div>
            </div>
          </div>

          <div className="section-group">
            <div className="section-label">SECURITY & MANAGEMENT</div>
            <div className="info-card-new">
              <div className="info-item" onClick={() => navigate('/account/change-password')}>
                <div className="info-item-left">
                  <div className="info-icon-wrapper security">
                    <LockIcon size={20} color="#ffffff" />
                  </div>
                  <div className="info-item-content">
                    <div className="info-item-label">Change Password</div>
                  </div>
                </div>
                <ChevronRightIcon size={20} color="#9ca3af" />
              </div>
              <div className="info-item" onClick={() => navigate('/account/edit-profile')}>
                <div className="info-item-left">
                  <div className="info-icon-wrapper security">
                    <EditIcon size={20} color="#ffffff" />
                  </div>
                  <div className="info-item-content">
                    <div className="info-item-label">Edit Profile</div>
                  </div>
                </div>
                <ChevronRightIcon size={20} color="#9ca3af" />
              </div>
              <div className="info-item" onClick={() => navigate('/account/help-support')}>
                <div className="info-item-left">
                  <div className="info-icon-wrapper security">
                    <QuestionIcon size={20} color="#ffffff" />
                  </div>
                  <div className="info-item-content">
                    <div className="info-item-label">Help & Support</div>
                  </div>
                </div>
                <ChevronRightIcon size={20} color="#9ca3af" />
              </div>
            </div>
          </div>

          <div className="section-group">
            <div className="section-label">APPEARANCE</div>
            <div className="info-card-new">
              <div className="info-item" onClick={toggleTheme}>
                <div className="info-item-left">
                  <div className="info-icon-wrapper appearance">
                    {theme === 'dark' ? (
                      <MoonIcon size={20} color="#ffffff" />
                    ) : (
                      <SunIcon size={20} color="#ffffff" />
                    )}
                  </div>
                  <div className="info-item-content">
                    <div className="info-item-label">Theme</div>
                    <div className="info-item-value">{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</div>
                  </div>
                </div>
                <div className="theme-toggle-switch">
                  <div className={`toggle-track ${theme === 'dark' ? 'dark' : ''}`}>
                    <div className="toggle-thumb"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="logout-section">
          <button className="logout-btn" onClick={handleSignOut}>
            <DoorIcon size={20} color="#ef4444" />
            <span>Log Out</span>
          </button>
        </div>

      </div>
    </div>
  );
};

export default Account;
